// Listing modes for Bookings — Calendar / List.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/flight_schedule/modes.ts, itself mirrored from
// src/blank/BlankLayout.tsx (Rule 0 — the canonical reference).
//
//   <base>/calendar → BookingsCalendar, calendar family (day-axis views)
//   <base>/timeline → BookingsCalendar, timeline family (the aircraft Gantt)
//   <base>/list     → BookingsList     (flat AeronetDataGrid over bookings)
//
// WHY THREE ROUTES OVER ONE COMPONENT, AND WHY THIS CHANGED. Legacy's six
// scheduler views (timeline / timeline_workweek / timeline_week /
// timeline_month / unit / grid) are NOT six modes — they are the
// `load_from_mode` argument to ONE surface, `mode=new_display`
// (flight_booking_new_display.inc), swapped by the three dropdown families in
// its toolbar. Only `grid` ("Agenda") is a different surface, and in v6 that
// is this module's existing list.
//
// That reading is still correct about legacy, and this file used to conclude
// from it that the route table should carry two SURFACES with the calendar
// owning its own view select. The conclusion did not survive contact with the
// screen: it left the aircraft timeline/Gantt — the view an operations
// controller opens this module for — two levels down, behind a secondary
// select inside the calendar toolbar, discoverable only by opening a dropdown
// labelled "View". Aaron called it in the 2026-09-03 demo review: promote the
// timeline views to a top-level breadcrumb mode.
//
// So `calendar` and `timeline` are two modes over ONE component
// (BookingsCalendar, `mode` prop), not two components. Mirroring legacy's
// data contract does not oblige us to mirror how legacy buried the view.
// Which families each mode offers: calendar.ts MODE_VIEW_FAMILIES.
import { CALENDAR_VIEWS, isCalendarView, isViewInMode } from "./calendar";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  calendar: "Calendar",
  timeline: "Timeline",
  list: "List",
  expenses: "Contracts",
  config: "Config",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  calendar: "Day and week views",
  timeline: "Bookings by aircraft",
  list: "Every booking",
  expenses: "Contract costs and billing",
  config: "Scheduler settings",
};

/**
 * Calendar, not List. Legacy config `flight_booking_default_mode` is
 * `new_display` on every tenant measured (g2 module_g2_demo, 2026-08-29) —
 * the scheduler is the surface an operator opens this module for.
 */
export const DEFAULT_MODE_SEGMENT = "calendar";

/** `<module>.viewMode`, matching every other converted module's key shape. */
export const MODE_STORAGE_KEY = "bookings.viewMode";

/**
 * `<module>.calendarView` — which of the calendar's OWN views was last shown.
 * Separate key from MODE_STORAGE_KEY: the mode is a route, the view is not.
 *
 * PER MODE, because the two modes offer disjoint view sets. One shared key
 * would have them overwrite each other: leave Timeline on "Timeline Week",
 * open Calendar (which cannot show it, so it falls back to "workweek" and
 * stores that), come back to Timeline and its remembered view is now a
 * calendar view it must reject. Each mode remembering its own last view is
 * also what an operator expects.
 */
export const CALENDAR_VIEW_STORAGE_KEY = "bookings.calendarView";

/** Per-mode view key — `bookings.calendarView.timeline`, etc. */
export const calendarViewStorageKey = (mode: string): string =>
  `${CALENDAR_VIEW_STORAGE_KEY}.${mode}`;

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: Bookings never shipped a chip+menu switcher, so
 * there is no pre-existing stored value in another shape to translate.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Last calendar view, validated the same hasOwnProperty way as the mode —
 * a persisted "__proto__" here would index CALENDAR_VIEWS to a function and
 * crash the renderer rather than 404 it, which is worse, not better.
 *
 * `mode` additionally requires the stored view to be one THIS mode offers, so
 * a value left by the other mode (or by the pre-split single key) is rejected
 * rather than rendering a calendar view inside Timeline. Omitting `mode`
 * keeps the original single-key behaviour for any caller that has no mode.
 */
export function readRememberedCalendarView(
  fallback: string,
  mode?: string
): string {
  if (typeof window === "undefined") return fallback;
  if (mode) {
    const scoped = window.localStorage.getItem(calendarViewStorageKey(mode));
    if (scoped && isViewInMode(scoped, mode)) return scoped;
    /* Fall through to the LEGACY unscoped key, so an operator who had a view
       remembered before this split keeps it when it is valid for the mode
       they land in. */
    const legacy = window.localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    return legacy && isViewInMode(legacy, mode) ? legacy : fallback;
  }
  const stored = window.localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
  return stored && isCalendarView(stored) ? stored : fallback;
}

/** Present only so an unused-import lint cannot hide a CALENDAR_VIEWS rename. */
export const CALENDAR_VIEW_COUNT = Object.keys(CALENDAR_VIEWS).length;

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
